import useQueryParams from './useQueryParams'
import useHistoryPush from './useHistoryPush'
import useIsMobileScreen from './useIsMobileScreen'
import getSidebarConfig from '../utils/helpers/getSidebarConfig'
import sidebarOptionWithAuth from '../utils/helpers/sidebarOptionWithAuth'
import SIDEBAR_OPTION from '../utils/constants/sidebarOptions'
import React from 'react'
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents'
import { isMobileRoute } from '../utils/constants/mobileScreen'
import usePairedCompetitions from './usePairedCompetitions'
import isOrgAddonsValid from '../utils/helpers/isOrgAddonsValid'
import { USER_ADDONS } from '../utils/constants/userAddons'
import ORGANISATION_TYPES from '../utils/constants/OrganisationTypes'
import COLORS from '../utils/constants/colors'

// The query a paired competition's pages carry (see useCmsScope); dropped on the way out.
const PAIRED_PARAMS = ['fedOrg', 'competition', 'seasonId']
// What a page leaves in the URL that the paired CMS must not pick up.
const PAGE_PARAMS = ['seasonId', 'eventId']

// Competitions grouped by federation, federations in order of their first competition.
const byFederation = (competitions) => {
  const groups = new Map()
  competitions.forEach((c) => groups.set(c.fedOrgId, [...(groups.get(c.fedOrgId) || []), c]))
  return [...groups.values()]
}

/**
 * Hook that manages all sidebar menu item logic
 * Returns the sidebar options to display and helper functions for rendering
 */
const useMenuItemLinks = (menuChoice, hasOrgs) => {
  const queryParams = useQueryParams()
  const { redirect } = useHistoryPush()
  const isMobileScreen = useIsMobileScreen()

  // Get the sidebar configuration
  const config = getSidebarConfig({
    menuChoice,
    queryParams,
    hasOrgs,
  })

  const { sidebarKey, isProfileItems, isTeamSettings } = config

  // Get the filtered sidebar options based on auth
  const itemList =
    SIDEBAR_OPTION[sidebarKey] ??
    SIDEBAR_OPTION.SIDEBAR_OPTIONS_BY_ORG_TYPE?.[sidebarKey] ??
    []
  // A phone only gets the pages that work there; the profile items all live on /profile.
  // A club holding ViewStats, with a team selected, gets one item per federation competition the
  // team is paired into, at the end of its sidebar, headed by the federation whose team it is
  // connected to. Competition names run long, so a federation's only one is just "Competition";
  // several keep their names, cut short, in full on hover.
  const viewsPairedStats = sidebarKey === ORGANISATION_TYPES.ORG_CLUB && isOrgAddonsValid([USER_ADDONS.VIEW_STATS])
  const { data: pairedCompetitions } = usePairedCompetitions(queryParams.org, queryParams.team, {
    enabled: viewsPairedStats && !isMobileScreen,
  })
  const pairedOptions = viewsPairedStats && !isMobileScreen
    ? byFederation(pairedCompetitions || []).flatMap((federation) => federation.map((competition, index) => ({
        name: `paired-cms:${competition.competitionId}`,
        text: federation.length === 1 ? 'Competition' : competition.name,
        fullText: competition.name,
        connectedFrom: index === 0 ? competition.fedOrgName || 'Connected' : undefined,
        icon: <EmojiEventsIcon />,
        iconSelected: <EmojiEventsIcon style={{ fill: COLORS.orange }} />,
        paired: competition,
      })))
    : []

  const sidebarOptions = [
    ...sidebarOptionWithAuth(itemList)
      .filter((option) => !option.requiresTeam || queryParams.team)
      .filter((option) => !isMobileScreen || isProfileItems || isMobileRoute(`/${option.name}`)),
    ...pairedOptions,
  ]

  /**
   * Mapper function to determine the click handler for a menu item
   * based on whether it's a profile item or regular menu item
   */
  const getMenuItemClickHandler = (item) => {
    if (item.paired) {
      return () => redirect('/paired-cms', [{ fedOrg: item.paired.fedOrgId }, { competition: item.paired.competitionId }], PAGE_PARAMS)
    }
    if (isProfileItems) {
      return () => redirect('/profile', [{ profileOption: item.name }], PAIRED_PARAMS)
    }
    if (item.name === 'calendar' && queryParams.team) {
      return () => redirect('/teamCalendar', [], PAIRED_PARAMS)
    }
    return () => (redirect(`/${item.name}`, [], PAIRED_PARAMS))
  }

  /**
   * Mapper function to determine which item is currently selected
   * teamCalendar maps to 'calendar' since they share one sidebar item
   */
  const getSelectedItemName = () => {
    if (isProfileItems) return queryParams.profileOption
    if (menuChoice === 'paired-cms') return `paired-cms:${queryParams.competition}`
    return menuChoice === 'teamCalendar' ? 'calendar' : menuChoice
  }

  /**
   * Returns display text for an item, adapting 'calendar' based on team selection
   */
  const getItemText = (item) => {
    if (item.name === 'calendar' && queryParams.team) return "Team's Calendar"
    return item.text
  }

  /**
   * Mapper function to determine if a menu item should be disabled
   * Note: Profile items have different disabled logic than regular items
   */
  const isMenuItemDisabled = (item, error) => {
    if (isProfileItems) {
      return !error || item?.disabled
    }
    return error || item?.disabled
  }

  /**
   * Mapper function to determine tooltip hover behavior
   * Note: Profile items use OR logic while regular items use AND logic
   */
  const shouldDisableTooltipHover = (item, error) => {
    if (isProfileItems) {
      return !item.disabled || !error
    }
    return !item.disabled && !error
  }

  return {
    selectedSidebarOption: sidebarKey,
    sidebarOptions,
    isProfileItems,
    isTeamSettings,
    getMenuItemClickHandler,
    getSelectedItemName,
    getItemText,
    isMenuItemDisabled,
    shouldDisableTooltipHover,
  }
}

export default useMenuItemLinks
